import React from "react";
import { createPortal } from "react-dom";
import { FileText, Printer, X } from "lucide-react";

/**
 * Modal that displays the pallet-rack fabricator cutting list (BOM).
 *
 * Uses a React portal so the modal is mounted directly on <body>, which
 * lets the print stylesheet cleanly hide the app shell and paginate the
 * BOM naturally without content repeating on every page.
 *
 * Props:
 *  - rows:    array of BOMLine returned by /api/calculate/bom or /api/quotations/{id}/bom
 *  - busy:    spinner while the request is in-flight
 *  - items:   array of {config, quantity, label?} — used for the header summary
 *  - client:  quotation client object (company / address) — appears in the print header
 *  - quoteNumber: optional quote_number string (only for saved quotations)
 *  - onClose: () => void
 */
export default function PalletBomModal({ rows, busy, items, client, quoteNumber, title = "Pallet Rack", onClose }) {
  // `rows` may be either a legacy flat array of BOMLine or (new) an array of
  // BOMGroup subsections. Detect the shape defensively.
  const groups = Array.isArray(rows) && rows.length && rows[0] && Array.isArray(rows[0].lines)
    ? rows
    : (rows && rows.length
        ? [{ item_index: 1, label: "", summary: "", quantity: 1, total_pieces: rows.reduce((s,r)=>s+(r.quantity||0),0), lines: rows }]
        : []);
  const total_pieces = groups.reduce((s, g) => s + (g.total_pieces || 0), 0);
  const today = new Date().toLocaleDateString("en-IN");

  React.useEffect(() => {
    document.body.classList.add("printing-bom");
    return () => document.body.classList.remove("printing-bom");
  }, []);

  const node = (
    <div
      className="pr-bom-overlay fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pr-bom-modal"
    >
      <div
        className="pr-bom-panel bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-line shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar (screen only) */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-surfaceAlt no-print">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-brand" />
            <div className="mono font-bold uppercase tracking-wider text-sm">
              Bill of Materials — {title} Cutting List
              {quoteNumber ? <span className="ml-2 text-ink-muted">· {quoteNumber}</span> : null}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn-primary" data-testid="pr-bom-print-btn">
              <Printer size={14} /> Print / PDF
            </button>
            <button onClick={onClose} className="btn-ghost"><X size={14} /></button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="pr-bom-body flex-1 overflow-y-auto p-6">
          {/* Print-only header (renders inline, so browser paginates naturally) */}
          <div className="print-only pb-3 border-b-2 border-black mb-4">
            <div className="flex items-center justify-between">
              <img
                src="/brand/adarsh-logo.png"
                alt="Adarsh Udyog"
                style={{ height: 44, objectFit: "contain" }}
              />
              <div className="text-center">
                <div className="font-black uppercase tracking-widest text-lg">{title.toUpperCase()} · BOM</div>
                <div className="text-[10px] mono">
                  {quoteNumber ? `${quoteNumber} · ` : ""}
                  {client?.company || client?.name || "—"}
                  {client?.address ? ` · ${client.address}` : ""}
                </div>
              </div>
              <div className="text-[10px] text-right mono">
                <div>Date: {today}</div>
                <div>All dims in MM</div>
              </div>
            </div>
          </div>

          <div className="text-xs mb-3 text-ink-muted">
            Cutting list grouped by line item · <b>{groups.length}</b> line{groups.length === 1 ? "" : "s"} ·
            total <b className="text-ink">{total_pieces}</b> pieces to fabricate.
          </div>

          {busy ? (
            <div className="p-10 text-center label-tech">Building BOM…</div>
          ) : groups.length === 0 ? (
            <div className="p-10 text-center label-tech italic text-ink-muted">No BOM lines.</div>
          ) : (
            groups.map((g) => (
              <div key={g.item_index} className="mb-6 bom-group" data-testid={`pr-bom-group-${g.item_index}`}>
                <div className="flex items-baseline justify-between pb-1.5 mb-2 border-b border-ink">
                  <div className="mono font-bold uppercase tracking-wider text-sm text-ink">
                    Item {g.item_index}
                    {g.label ? <span className="ml-2 text-ink-muted normal-case tracking-normal">· {g.label}</span> : null}
                  </div>
                  <div className="text-[0.7rem] mono text-ink-muted">
                    {g.total_pieces} pieces
                  </div>
                </div>
                <div className="text-[0.75rem] mono text-ink-muted mb-2">{g.summary}</div>
                <div className="panel-heavy">
                  <table className="w-full grid-lines">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Component</th>
                        <th>Section</th>
                        <th className="num">T (mm)</th>
                        <th className="num">Width (mm)</th>
                        <th className="num">Length (mm)</th>
                        <th className="num">Qty</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.lines.map((r, i) => (
                        <tr key={i} data-testid={`pr-bom-row-${g.item_index}-${i}`}>
                          <td className="mono num">{i + 1}</td>
                          <td className="font-bold uppercase tracking-wider text-xs">{r.component}</td>
                          <td className="mono text-xs">{r.section || "—"}</td>
                          <td className="num mono text-xs">{r.thickness_mm > 0 ? r.thickness_mm : "—"}</td>
                          <td className="num mono">{r.width_mm > 0 ? r.width_mm : "—"}</td>
                          <td className="num mono">{r.length_mm > 0 ? r.length_mm : "—"}</td>
                          <td className="num mono font-bold">{r.quantity}</td>
                          <td className="text-[0.7rem] text-ink-muted">{r.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return node; // SSR safety
  return createPortal(node, document.body);
}
